import { gql } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import Image from '../../Image';
import { Sizes } from '../../../constants/Image';
import LocalizedLink from '../../LocalizedLink';
import { ArrowLongRightIcon } from '@heroicons/react/24/outline';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import TopProducts from '../TopProducts/TopProducts';
import { useQuery } from '@apollo/client/react';

const GET_CATEGORIES = gql`
  query CategoriesMain {
    categoriesMain {
      id
      title
      slug
    }
  }
`;

type MainCategoryItem = {
  id: string;
  title: string;
  slug: string;
};

const GET_BANNER = gql`
  query Banners($slot: String) {
    banners(slot: $slot) {
      slug
      bannerType
      image {
        urls {
          W435
          W540
          W890
          W1600
        }
      }
      buttons {
        buttonType
        title
        url
        buttonGroup
        brand {
          logoUrl
        }
      }
      title
      content
    }
  }
`;

export type Banner = {
  slug: string;
  bannerType: string;
  buttons: {
    buttonType: string;
    title: string;
    url: string;
    buttonGroup: string;
    brand: {
      logoUrl: string;
    };
  }[];
  image: {
    urls: {
      W435: string;
      W540: string;
      W890: string;
      W1600: string;
    };
  };
  title: string;
  content: string;
};

const CATEGORY_SLOT_MAP: { [key: string]: string } = {
  naised: 'home-women',
  mehed: 'home-men',
  lapsed: 'home-kids',
};

export default function CategoryBlock() {
  const [category, setCategory] = useState('naised');
  const { data: categoryData, loading: categoryLoading } = useQuery<{
    categoriesMain: MainCategoryItem[];
  }>(GET_CATEGORIES);

  const slot = CATEGORY_SLOT_MAP[category] || 'home-women';

  const { data: bannerData } = useQuery<{ banners: Banner[] }>(GET_BANNER, {
    variables: { slot },
  });
  const banners = bannerData?.banners ?? [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categoryChange = (slug: string) => {
    setCategory(slug);
  };

  const categories = categoryData?.categoriesMain;

  let options;
  if (!categoryLoading && categories) {
    options = categories.map((option, index) => {
      const isActive = category === option.slug;

      return (
        <div
          key={index}
          onClick={() => categoryChange(option.slug)}
          className={`flex flex-nowrap snap-center cursor-pointer after:content-['_/_'] last:after:content-[''] after:mx-2 md:after:mx-6 after:text-3xl md:after:text-4xl after:text-rgrey ${
            isActive ? 'text-black' : 'text-rgrey'
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-axi-bold leading-10">
            {option.title}
          </h2>
        </div>
      );
    });
  }

  return (
    <div className="mb-16 xl:mb-18 mt-16 xl:mt-28">
      <div className="flex overflow-x-auto overflow-y-hidden! pb-1 sm:pb-0 scrollbar scrollbar-h-0.5 scrollbar-thumb-rgrey scrollbar-track-rgrey-lightlight scrollbar-thumb-rounded-xs scrollbar-track-rounded-xs">
        {options}
      </div>
      {banners.length > 0 ? (
        <div className="relative pt-6">
          <Swiper
            modules={[Navigation, Pagination]}
            spaceBetween={16}
            slidesPerView={'auto'}
            pagination={{
              el: '.category-swiper-pagination',
              clickable: true,
            }}
            navigation={{
              nextEl: '.category-swiper-button-next',
              prevEl: '.category-swiper-button-prev',
            }}
            loop={false}
          >
            {banners.map((banner: Banner) => (
              <SwiperSlide key={banner.slug} className="w-72! h-96 shrink-0">
                <LocalizedLink
                  to={banner?.buttons[0]?.url}
                  className="group shrink-0 relative block w-72 h-96 overflow-hidden"
                  ariaLabel={banner?.title || 'Uuri lisaks'}
                >
                  <Image
                    className="bg-rgrey-lightlight object-cover h-full w-full transition-transform duration-300 ease-in-out"
                    image={banner.image}
                    defaultSize={Sizes.W890}
                    allSizes={[Sizes.W890, Sizes.W1600]}
                    sizes={['(max-width: 1024px) 90vw, 50vw']}
                    alt={banner.title || 'Rademar'}
                  />
                  {/* Black overlay for opacity */}
                  <div className="absolute inset-0 bg-black opacity-50"></div>
                  {banner.title && (
                    <div className="absolute bottom-0 left-0 w-full p-4 z-10">
                      <span className="block text-white text-2xl font-axi-bold p-2 transition-transform duration-200 ease-in-out group-hover:-translate-y-5 group-focus-within:-translate-y-5">
                        {banner.title}
                      </span>
                      <span className="block text-white text-sm font-axi-book p-2 absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-all duration-200 ease-in-out">
                        Uuri lisaks
                        <ArrowLongRightIcon className="w-4 h-4 inline-block ml-1" />
                      </span>
                    </div>
                  )}
                </LocalizedLink>
              </SwiperSlide>
            ))}
          </Swiper>

          <div className="flex justify-start items-center gap-x-4 mt-12 -ml-1">
            <button
              aria-label="eelmine"
              className="swiper-button-prev category-swiper-button-prev"
            />
            <div className="swiper-pagination category-swiper-pagination" />
            <button
              aria-label="järgmine"
              className="swiper-button-next category-swiper-button-next"
            />
          </div>
        </div>
      ) : null}
      <TopProducts category={category} />
    </div>
  );
}
